import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Button from "../../components/GeneralComponents/Button";
import DomeCareTitle from "../../components/GeneralComponents/DomeCareTitle";
import GeneralInput from "../../components/GeneralComponents/GeneralInput";
import ModalPhoneNumberInput from "../../components/GeneralComponents/ModalPhoneNumberInput";
import VerifiyNumberPopup from "../../components/Popups/VerifiyNumberPopup";
import history from "../../hooks/History";
import {
  checkFromNumber,
  getRegisterUserOTPInfo,
  setIsUserRegistering,
} from "../../redux/slices/authSlice";
import { RootState } from "../../redux/store";
import { RegisterFormValues } from "../../types";
import { useCheckFromNumber } from "../../utils/api/AuthAPIs/useCheckFromNumber";
import { useCountriesQuery } from "../../utils/api/General/useCountries";
import {
  ArabicCode,
  DefaultNavigateDelay,
  EnglishCode,
} from "../../utils/constants/CommonConstants";
import useRegisterSchema from "../../utils/validations/auth/registerSchema";
import CustomCheckbox from "../../components/GeneralComponents/CustomCheckbox";
import { setRegisterForm } from "../../redux/slices/generalSlice";

const Register: React.FC = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation("translation");
  const registerSchema = useRegisterSchema();
  //?====================States====================
  const [isRegexError, setIsRegexError] = useState<boolean>(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const { phoneNumber, username, countryId, countryData } = useSelector(
    (state: RootState) => state.auth,
  );
  const { registerForm } = useSelector((state: RootState) => state.general);

  const initialValues: RegisterFormValues = {
    phoneNumber: registerForm?.phoneNumber || "",
    username: registerForm?.username || "",
    privacy: registerForm?.privacy || false,
  };
  // ?===================Get Countries================
  const { data: CountriesData, isLoading: CountriesIsLoading } =
    useCountriesQuery();

  // ?===================POST Check From Number================

  const {
    error: checkFromNumberError,
    mutate: checkFromNumberMutation,
    status: checkFromNumberStatus,
    reset: CheckFromNumberReset,
  } = useCheckFromNumber();

  // ?===================Submit Form Function================
  const handleSubmit = (values: RegisterFormValues) => {
    if (!isRegexError) {
      setShowConfirmPopup(() => true);
      dispatch(
        checkFromNumber({
          phoneNumber: values.phoneNumber,
          username: values.username,
        }),
      );
    }
  };
  // ?===================Confirm Modal Submit================
  const handleNavigateToOTP = async () => {
    checkFromNumberMutation(
      {
        CountryId: countryId,
        FullName: username,
        PhoneNumber: phoneNumber,
        Language: i18n.language.slice(0, 2),
      },
      {
        onSuccess: (data) => {
          dispatch(setIsUserRegistering(true));
          dispatch(
            setRegisterForm({ phoneNumber: "", username: "", privacy: false }),
          );
          dispatch(
            getRegisterUserOTPInfo({
              userId: data.Data.Id,
              otpDuration: data?.Data.Duration,
              otpType: "register",
            }),
          );
          history.push("/auth/otp");
        },
        onError: (error) => {
          console.log(error);
        },
      },
    );
  };

  return (
    <article className="z-20 flex w-full flex-col gap-6 bg-white px-8 py-10 xl:w-[75%]">
      <div className="flex flex-col gap-6">
        {/* <DomeCareTitle /> */}
        <p className="py-8 text-center text-2xl font-bold text-headingTextColor">
          {t("create_an_account_at_domecare")}
        </p>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={registerSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, values, setFieldValue, errors }) => {
          return (
            <Form
              className="flex flex-col gap-5"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setTimeout(() => {
                    handleSubmit();
                  }, DefaultNavigateDelay);
                }
              }}
            >
              <div className="flex flex-col gap-5">
                <GeneralInput
                  name="username"
                  type="text"
                  required={true}
                  placeholder={
                    i18n.language.slice(0, 2) === EnglishCode
                      ? t("your_name_in_english")
                      : t("your_name_in_arabic")
                  }
                  arabicOnly={i18n.language.slice(0, 2) === ArabicCode}
                  englishOnly={i18n.language.slice(0, 2) === EnglishCode}
                  resetOnchangeLanguage={true}
                  autoFocus={true}
                />
                <ModalPhoneNumberInput
                  name="phoneNumber"
                  placeholder={t("phone_number")}
                  getRegexError={(isRegexError) => {
                    setIsRegexError(isRegexError);
                  }}
                  countriesList={CountriesData?.Data}
                  numericOnly={true}
                />
                <div className="flex flex-col items-start gap-2">
                  <div className="flex items-center gap-2">
                    <CustomCheckbox
                      checked={values.privacy}
                      id="privacy"
                      name="privacy"
                      onChange={(event) => {
                        setFieldValue("privacy", event.target.checked);
                      }}
                    />
                    <p className="font-semibold capitalize text-headingTextColor">
                      {t("i agree")}{" "}
                      <span className="font-semibold text-primaryMain underline">
                        <Link
                          onClick={() => {
                            dispatch(
                              setRegisterForm({
                                phoneNumber: values.phoneNumber,
                                username: values.username,
                                privacy: values.privacy,
                              }),
                            );
                          }}
                          to={"/PrivacyPolicy"}
                        >
                          {t("privacy policy")}
                        </Link>
                      </span>
                    </p>
                  </div>
                  {errors["privacy"] && (
                    <p className="mt-1 text-sm font-semibold text-errorMain">
                      {errors["privacy"]}
                    </p>
                  )}
                </div>
              </div>
              <Button
                title={CountriesIsLoading ? t("loading") : t("create_account")}
                type="submit"
                variant="primary"
                extraClassName="shadow-cta text-lg"
                disabled={CountriesIsLoading}
              />
            </Form>
          );
        }}
      </Formik>

      <div className="flex items-center justify-center gap-2 px-8 py-3">
        <p className="text-base font-normal text-headingTextColor">
          {t("already_have_account")}
        </p>
        <p className="text-base font-normal text-primaryMain">
          <Link
            onClick={() =>
              dispatch(
                setRegisterForm({
                  phoneNumber: "",
                  username: "",
                  privacy: false,
                }),
              )
            }
            to={"/auth/login"}
            className="text-base font-bold capitalize text-secondaryMain underline"
          >
            {t("login")}
          </Link>
        </p>
      </div>
      {showConfirmPopup && (
        <VerifiyNumberPopup
          setShow={setShowConfirmPopup}
          show={showConfirmPopup}
          countryZipCode={countryData?.ZipCode}
          phoneNumber={phoneNumber}
          error={checkFromNumberError}
          navigateAfterSuccessFunction={handleNavigateToOTP}
          editButtonOnClickFunction={() => {
            setShowConfirmPopup(false);
            CheckFromNumberReset();
          }}
          disableButtonCondition={checkFromNumberStatus === "pending"}
        />
      )}
    </article>
  );
};

export default Register;
